import { BannerInputs } from './bannerInputs';

export type { BannerSlot } from './bannerInputs';

export type BannerVariant =
  | 'C1'
  | 'C2'
  | 'C3'
  | 'C4'
  | 'C5'
  | 'C6'
  | 'C7'
  | 'O1'
  | 'O2'
  | 'O3'
  | 'O4';

export type Dismissibility = 'never' | 'noticeWindow' | 'session' | 'existing';

type Derived = {
  heldOn: boolean;
  available: boolean;
  returnedRecently: boolean;
  degradedPastThreshold: boolean;
};

type PrecedenceRow = {
  variant: BannerVariant;
  slot: BannerInputs['viewing'];
  fires: (inputs: BannerInputs, derived: Derived) => boolean;
};

const PRECEDENCE: readonly PrecedenceRow[] = [
  {
    variant: 'O2',
    slot: 'offline',
    fires: (i, d) => d.heldOn && i.probedThisLaunch && i.onLocalNetwork === false,
  },
  { variant: 'C1', slot: 'cloud', fires: (_i, d) => d.available },
  {
    variant: 'O1',
    slot: 'offline',
    fires: (i, d) => d.heldOn && i.onLocalNetwork !== false && i.registered !== false,
  },
  { variant: 'C2', slot: 'cloud', fires: (_i, d) => d.heldOn && !d.available },
  {
    variant: 'C7',
    slot: 'cloud',
    fires: (i) => i.held === null && i.replica?.isActive === true,
  },
  { variant: 'C3', slot: 'cloud', fires: (_i, d) => d.returnedRecently },
  { variant: 'O4', slot: 'offline', fires: (_i, d) => d.returnedRecently },
  {
    variant: 'O3',
    slot: 'offline',
    fires: (i, d) => i.testOverrideActive && !d.heldOn,
  },
  {
    variant: 'C4',
    slot: 'cloud',
    fires: (i) => i.held === null && i.heartbeatStale === true && i.replica?.isActive !== true,
  },
  {
    variant: 'C5',
    slot: 'cloud',
    fires: (i, d) => d.degradedPastThreshold && i.orgProvisionedForOfflineMode,
  },
  { variant: 'C6', slot: 'cloud', fires: (i) => i.connectivity.degraded },
];

const DISMISSIBILITY: Record<BannerVariant, Dismissibility> = {
  C1: 'never',
  C2: 'never',
  C3: 'noticeWindow',
  C4: 'session',
  C5: 'existing',
  C6: 'existing',
  C7: 'never',
  O1: 'never',
  O2: 'never',
  O3: 'never',
  O4: 'never',
};

function derive(inputs: BannerInputs): Derived {
  const { held, nowMs, onLocalNetwork, registered, connectivity, degradedSinceMs } = inputs;
  const heldOn = held?.isActive === true;
  const heldOff = held !== null && !held.isActive;
  const returnedRecently = heldOff && nowMs - held.updatedAt < inputs.returnNoticeWindowMs;

  return {
    heldOn,
    available: heldOn && onLocalNetwork === true && registered !== false,
    returnedRecently,
    degradedPastThreshold:
      connectivity.degraded &&
      degradedSinceMs != null &&
      nowMs - degradedSinceMs >= inputs.c5ThresholdMs,
  };
}

export function resolveBanner(inputs: BannerInputs): BannerVariant | null {
  const derived = derive(inputs);
  const row = PRECEDENCE.find(
    (candidate) => candidate.slot === inputs.viewing && candidate.fires(inputs, derived)
  );

  return row ? row.variant : null;
}

export function dismissibilityOf(variant: BannerVariant): Dismissibility {
  return DISMISSIBILITY[variant];
}
